/** @jsx jsx */
import { gql } from '@apollo/client';
import { SemiTitle, Text } from '@bottlebooks/gatsby-design-system';
import { Col, Container, Link, Row } from '@bottlebooks/gatsby-theme-base';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
import { Map, Marker } from '../Map/Map';

export default function BrandAddressSection({ brand, ...rest }) {
  const { i18n } = useLingui();
  if (!brand) return null;
  const {
    street,
    postalCode,
    city,
    telephone,
    email,
    website,
    name,
    latitude,
    longitude,
    facebook,
  } = brand;

  // TODO Remove this workaround for inconsistent handling of street lines (on the server it is as single string, in Gatsby it's an array of strings).
  const streetLines = typeof street === 'string' ? street.split('\n') : street;
  const shortAddress = [
    ...(streetLines || []),
    `${postalCode || ''} ${city || ''}`,
  ]
    .join(', ')
    .trim();
  // Normalizes URLs by adding http:// in front.
  const websiteUrl =
    website && `http://${website.trim()}`.replace(/^http:\/\/http/, 'http');
  const facebookUrl = getFacebookURL(facebook);
  return (
    <Container fluid sx={{ backgroundColor: 'light', marginY: 5 }} {...rest}>
      <Row>
        <Col sx={{ padding: 0, width: ['100%', '50%'], bg: 'hover' }}>
          {latitude != null && longitude != null && (
            <Map
              initialCenter={{ lat: latitude, lng: longitude }}
              zoom={13}
              sx={{ minHeight: ['50vw', '25vw'] }}
            >
              <Marker
                title={name}
                position={{ lat: latitude, lng: longitude }}
              />
            </Map>
          )}
        </Col>
        <Col
          sx={{
            paddingX: [null, `${100 / 12}%`],
            width: ['100%', '50%'],
            paddingY: 5,
            alignSelf: 'center',
          }}
        >
          <AddressSegment title={i18n._(t`Address`)}>
            {shortAddress}
          </AddressSegment>
          <AddressSegment title={i18n._(t`Telephone`)}>
            {telephone}
          </AddressSegment>
          <AddressSegment title={i18n._(t`Email`)}>
            {email && (
              <TouchableLink href={`mailto:${email}`}>{email}</TouchableLink>
            )}
          </AddressSegment>
          <AddressSegment title={i18n._(t`Website`)}>
            {website && (
              <TouchableLink
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {website}
              </TouchableLink>
            )}
          </AddressSegment>
          <AddressSegment title={i18n._(t`Facebook`)}>
            {facebook && (
              <TouchableLink
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {facebook}
              </TouchableLink>
            )}
          </AddressSegment>
        </Col>
      </Row>
    </Container>
  );
}

function TouchableLink(props) {
  // Returns a link with enough padding to be touched with a finger (at least ~40px).
  return (
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    <Link sx={{ padding: 1, margin: -1, display: 'inline-block' }} {...props} />
  );
}

function AddressSegment({ title, children }) {
  if (!children) return null;
  return (
    <div sx={{ display: 'flex', paddingBottom: 2 }}>
      <SemiTitle
        title={title}
        sx={{ color: 'primary', width: '2em', padding: 0, margin: 0 }}
      >
        {title[0]}:
      </SemiTitle>
      <Text>{children}</Text>
    </div>
  );
}

function getFacebookURL(facebook = '') {
  if (!facebook) return undefined;
  if (facebook.startsWith('http')) return facebook;
  if (facebook.startsWith('www')) return `https://${facebook}`;
  if (facebook.startsWith('facebook.com')) return `https://${facebook}`;
  if (facebook.includes('/')) return facebook;
  if (facebook.includes(' ')) {
    return `https://facebook.com/public/${encodeURIComponent(facebook)}`;
  }
  if (facebook.startsWith('@')) {
    return `https://facebook.com/${encodeURIComponent(
      facebook.replace('@', '')
    )}`;
  }
  return `https://facebook.com/${encodeURIComponent(facebook)}`;
}

export const fragment = graphql`
  fragment BrandAddressSection on AbstractBrand {
    street
    postalCode
    city
    telephone
    email
    website
    facebook
    # ...BrandMap
    name
    latitude
    longitude
  }
`;

// Client-only pages need a separate fragment. Unfortunately both must be defined separately.
export const BrandAddressSectionFragment = gql`
  fragment BrandAddressSection on AbstractBrand {
    street
    postalCode
    city
    telephone
    email
    website
    # ...BrandMap
    name
    latitude
    longitude
  }
`;
